import { useMemoizedFn, useUpdateEffect } from 'ahooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { useRequestController } from '../../Home/hooks/useRequestController';
import { useErrorTips } from '@/pages/aigc/hooks/useErrorTips';
import { FILE_LIBRARY_PAGE_SIZE } from '../constants';
import { resolveApiFileTypes } from '../utils';
import type {
  TDatabrainFile,
  TFileLibraryQuery,
  TListDatabrainFilesParams,
  TListDatabrainFilesResponse,
} from '../types';

/** list_databrain_files 公共请求配置 */
const LIST_FILES_REQUEST = {
  method: 'POST' as const,
  url: '/api/agent_com/list_databrain_files' as const,
  httpCfg: { rawData: true, ignoreCode: true },
};

const CHINA_UTC_OFFSET = 8;

/** 筛选条件变化时，组装 list 请求体 */
const toListParams = (
  query: TFileLibraryQuery,
  pageNum: number,
): TListDatabrainFilesParams => {
  const conditions: TListDatabrainFilesParams['conditions'] = {};

  if (query.fileType) {
    conditions.file_type = resolveApiFileTypes(query.fileType);
  }

  if (query.timeRange) {
    const today = moment().utcOffset(CHINA_UTC_OFFSET).startOf('day');
    const endTime = today.clone().endOf('day').format('YYYY-MM-DD HH:mm:ss');
    if (query.timeRange === 'today') {
      conditions.start_time = today.format('YYYY-MM-DD HH:mm:ss');
      conditions.end_time = endTime;
    } else {
      const days = query.timeRange === '7d' ? 7 : 30;
      conditions.start_time = today
        .clone()
        .subtract(days, 'days')
        .format('YYYY-MM-DD HH:mm:ss');
      conditions.end_time = endTime;
    }
  }

  if (query.source?.kind === 'agent' && query.source.id) {
    conditions.model_id = [query.source.id];
  }
  if (query.source?.kind === 'skill' && query.source.skillName) {
    conditions.skills_used = [query.source.skillName];
  }
  if (query.keyword.trim()) {
    conditions.file_name_fuzzy_search = query.keyword.trim();
  }

  return {
    trace_id: uuidv4(),
    page_num: pageNum,
    page_size: FILE_LIBRARY_PAGE_SIZE,
    upload_type: query.uploadType,
    order_by_asc: { [query.sort.key]: query.sort.asc },
    conditions,
  };
};

/**
 * 文件库列表：首屏 auto（refreshDeps）+ loadMore 独立 manual 实例。
 * @param query - 筛选 / 排序 / Tab
 * @param scrollContainerRef - 筛选变化时滚回顶部
 */
export const useFileLibraryList = (
  query: TFileLibraryQuery,
  scrollContainerRef: RefObject<HTMLElement>,
) => {
  const showErrorTips = useErrorTips();
  const loadMoreInFlightRef = useRef(false);
  /** 查询版本号：查询变化（切页签 / 改筛选）自增，用于丢弃在途的过期 loadMore 响应 */
  const listVersionRef = useRef(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFiles, setMoreFiles] = useState<TDatabrainFile[]>([]);
  const [moreNoMore, setMoreNoMore] = useState(false);

  const listParams = useMemo(
    () => toListParams(query, 1),
    [
      query.uploadType,
      query.keyword,
      query.fileType,
      query.timeRange,
      query.source?.kind,
      query.source?.id,
      query.source?.skillName,
      query.sort.key,
      query.sort.asc,
    ],
  );

  const { data, loading } = useRequestController({
    ...LIST_FILES_REQUEST,
    refreshDeps: listParams,
    onSuccess: (response: TListDatabrainFilesResponse) => {
      showErrorTips?.(response?.res_code);
    },
  });

  const { runAsync: fetchPageAsync } = useRequestController({
    ...LIST_FILES_REQUEST,
    manual: true,
    onSuccess: (response: TListDatabrainFilesResponse) => {
      showErrorTips?.(response?.res_code);
    },
  });

  useEffect(() => {
    // 查询变化：标记新版本并复位分页态，同时清掉可能残留的在途 loading
    listVersionRef.current += 1;
    setMoreFiles([]);
    setMoreNoMore(false);
    loadMoreInFlightRef.current = false;
    setLoadingMore(false);
  }, [listParams]);

  useUpdateEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTop = 0;
    }
  }, [listParams, scrollContainerRef]);

  const firstPageFiles = useMemo(() => {
    const response = data as TListDatabrainFilesResponse | undefined;
    if (response?.res_code !== 0) {
      return [];
    }
    return response.files ?? [];
  }, [data]);

  const files = useMemo(() => {
    if (loading && !loadingMore) {
      return [];
    }
    return [...firstPageFiles, ...moreFiles];
  }, [firstPageFiles, loading, loadingMore, moreFiles]);

  const noMore = useMemo(() => {
    if (moreFiles.length > 0) {
      return moreNoMore;
    }
    return firstPageFiles.length < FILE_LIBRARY_PAGE_SIZE;
  }, [firstPageFiles.length, moreFiles.length, moreNoMore]);

  const loadMore = useMemoizedFn(async (): Promise<void> => {
    if (noMore || loadingMore || loading || loadMoreInFlightRef.current) {
      return;
    }
    const version = listVersionRef.current;
    loadMoreInFlightRef.current = true;
    setLoadingMore(true);
    try {
      const pageNum =
        Math.floor(
          (firstPageFiles.length + moreFiles.length) / FILE_LIBRARY_PAGE_SIZE,
        ) + 1;
      const response = (await fetchPageAsync(
        toListParams(query, pageNum),
      )) as TListDatabrainFilesResponse;
      // 请求期间查询已变化（如切换页签）：丢弃过期分页结果
      if (version !== listVersionRef.current) {
        return;
      }
      const pageFiles = response?.res_code === 0 ? response.files ?? [] : [];
      setMoreFiles(prev => [...prev, ...pageFiles]);
      setMoreNoMore(pageFiles.length < FILE_LIBRARY_PAGE_SIZE);
    } finally {
      // 仅当前版本复位，避免误清新查询已发起的新一轮 loadMore 状态
      if (version === listVersionRef.current) {
        loadMoreInFlightRef.current = false;
        setLoadingMore(false);
      }
    }
  });

  return {
    files,
    loading,
    loadingMore,
    loadMore,
    noMore,
  };
};
